/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type UploadTherapistDocumentInput } from '@therapy/shared-validators';

import { type TherapistDocument, therapistDocumentsApi } from '../api/therapist-documents.js';

const docsKey = (therapistId: string): readonly unknown[] => ['therapist-documents', therapistId];
const MY_DOCS_KEY = ['therapist-documents', 'mine'] as const;

export function useTherapistDocuments(
  therapistId: string,
): UseQueryResult<readonly TherapistDocument[]> {
  return useQuery({
    queryKey: docsKey(therapistId),
    queryFn: () => therapistDocumentsApi.list(therapistId),
    enabled: therapistId !== '',
  });
}

export function useMyDocuments(): UseQueryResult<readonly TherapistDocument[]> {
  return useQuery({ queryKey: MY_DOCS_KEY, queryFn: () => therapistDocumentsApi.listMine() });
}

export function useUploadTherapistDocument(
  therapistId: string,
): UseMutationResult<TherapistDocument, Error, UploadTherapistDocumentInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadTherapistDocumentInput) =>
      therapistDocumentsApi.upload(therapistId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: docsKey(therapistId) });
    },
  });
}

export function useDeleteTherapistDocument(
  therapistId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (docId: string) => therapistDocumentsApi.remove(therapistId, docId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: docsKey(therapistId) });
    },
  });
}
